// Gradient Builder - Type-Specific Controls Component
// Controls for Linear (angle), Radial (center, radius), Sweep (angles)

import React from "react";
import { GradientConfig } from "../../../model/gradient";
import { colors, typography, effects } from "../../../design/tokens";

interface GradientTypeControlsProps {
  config: GradientConfig;
  onChange: (config: GradientConfig) => void;
}

export const GradientTypeControls: React.FC<GradientTypeControlsProps> = ({
  config,
  onChange,
}) => {
  // Common slider styles
  const sliderClasses = `flex-1 h-2 ${colors.surface.secondary} ${effects.borderRadius.lg} appearance-none cursor-pointer
    [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-dark-text [&::-webkit-slider-thumb]:cursor-pointer [&::-webkit-slider-thumb]:shadow-sm
    [&::-moz-range-thumb]:w-4 [&::-moz-range-thumb]:h-4 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-dark-text [&::-moz-range-thumb]:border-0 [&::-moz-range-thumb]:cursor-pointer`;

  const numberInputClasses = `w-16 px-2 py-1 ${colors.surface.secondary} ${colors.text.primary} ${typography.size.sm} text-center ${effects.borderRadius.default} border ${colors.border.primary} focus:outline-none focus:border-primary-blue`;

  const compassButtonClasses = (isActive: boolean) => `
    px-3 py-2 ${typography.size.sm} ${effects.borderRadius.default} border ${effects.transition.default}
    ${isActive
      ? `border-dark-text/30 ${colors.surface.primary} ${colors.text.primary}`
      : `${colors.border.primary} ${colors.surface.secondary} ${colors.text.secondary} hover:text-dark-text hover:border-dark-border`
    }
  `;

  const emptyCompassCell = `px-3 py-2 ${effects.borderRadius.default} border ${colors.border.primary} ${colors.surface.secondary} opacity-30`;

  if (config.type === "linear") {
    return (
      <div className="space-y-3">
        <label className={`${typography.size.sm} ${colors.text.secondary} ${typography.weight.medium}`}>
          Angle
        </label>
        <div className="flex items-center gap-3">
          <input
            type="range"
            min="0"
            max="360"
            value={config.angle}
            onChange={(e) =>
              onChange({ ...config, angle: parseInt(e.target.value) })
            }
            className={sliderClasses}
          />
          <input
            type="number"
            min="0"
            max="360"
            value={config.angle}
            onChange={(e) =>
              onChange({
                ...config,
                angle: Math.max(0, Math.min(360, parseInt(e.target.value) || 0)),
              })
            }
            className={numberInputClasses}
          />
          <span className={`${typography.size.sm} ${colors.text.secondary}`}>°</span>
        </div>

        {/* Direction Presets - Compass layout with empty center */}
        <div className="grid grid-cols-3 gap-2">
          {[
            { label: "↖", angle: 135 },
            { label: "↑", angle: 90 },
            { label: "↗", angle: 45 },
            { label: "←", angle: 180 },
            null, // Empty center
            { label: "→", angle: 0 },
            { label: "↙", angle: 225 },
            { label: "↓", angle: 270 },
            { label: "↘", angle: 315 },
          ].map((item) =>
            item === null ? (
              <div key="center" className={emptyCompassCell} />
            ) : (
              <button
                key={item.angle}
                onClick={() => onChange({ ...config, angle: item.angle })}
                className={compassButtonClasses(config.angle === item.angle)}
              >
                {item.label}
              </button>
            )
          )}
        </div>
      </div>
    );
  }

  if (config.type === "radial") {
    return (
      <div className="space-y-4">
        {/* Center Position - Compass layout */}
        <div className="space-y-2">
          <label className={`${typography.size.sm} ${colors.text.secondary} ${typography.weight.medium}`}>
            Center Position
          </label>
          <div className="grid grid-cols-3 gap-2">
            {[
              null, // Top-left empty
              { label: "↑", value: "topCenter" },
              null, // Top-right empty
              { label: "←", value: "centerLeft" },
              { label: "●", value: "center" },
              { label: "→", value: "centerRight" },
              null, // Bottom-left empty
              { label: "↓", value: "bottomCenter" },
              null, // Bottom-right empty
            ].map((item, index) =>
              item === null ? (
                <div key={`empty-${index}`} className={emptyCompassCell} />
              ) : (
                <button
                  key={item.value}
                  onClick={() => onChange({ ...config, center: item.value as any })}
                  className={compassButtonClasses(config.center === item.value)}
                >
                  {item.label}
                </button>
              )
            )}
          </div>
        </div>

        {/* Radius */}
        <div className="space-y-2">
          <label className={`${typography.size.sm} ${colors.text.secondary} ${typography.weight.medium}`}>
            Radius
          </label>
          <div className="flex items-center gap-3">
            <input
              type="range"
              min="0.1"
              max="2.0"
              step="0.1"
              value={config.radius}
              onChange={(e) =>
                onChange({ ...config, radius: parseFloat(e.target.value) })
              }
              className={sliderClasses}
            />
            <input
              type="number"
              min="0.1"
              max="2.0"
              step="0.1"
              value={config.radius}
              onChange={(e) =>
                onChange({
                  ...config,
                  radius: Math.max(
                    0.1,
                    Math.min(2.0, parseFloat(e.target.value) || 0.1)
                  ),
                })
              }
              className={numberInputClasses}
            />
          </div>
        </div>
      </div>
    );
  }

  if (config.type === "sweep") {
    const startDeg = Math.round((config.startAngle * 180) / Math.PI);
    const endDeg = Math.round((config.endAngle * 180) / Math.PI);

    return (
      <div className="space-y-4">
        {/* Center Position - Compass layout */}
        <div className="space-y-2">
          <label className={`${typography.size.sm} ${colors.text.secondary} ${typography.weight.medium}`}>
            Center Position
          </label>
          <div className="grid grid-cols-3 gap-2">
            {[
              null, // Top-left empty
              { label: "↑", value: "topCenter" },
              null, // Top-right empty
              { label: "←", value: "centerLeft" },
              { label: "●", value: "center" },
              { label: "→", value: "centerRight" },
              null, // Bottom-left empty
              { label: "↓", value: "bottomCenter" },
              null, // Bottom-right empty
            ].map((item, index) =>
              item === null ? (
                <div key={`empty-${index}`} className={emptyCompassCell} />
              ) : (
                <button
                  key={item.value}
                  onClick={() => onChange({ ...config, center: item.value as any })}
                  className={compassButtonClasses(config.center === item.value)}
                >
                  {item.label}
                </button>
              )
            )}
          </div>
        </div>

        {/* Start Angle */}
        <div className="space-y-2">
          <label className={`${typography.size.sm} ${colors.text.secondary} ${typography.weight.medium}`}>
            Start Angle
          </label>
          <div className="flex items-center gap-3">
            <input
              type="range"
              min="0"
              max="360"
              value={startDeg}
              onChange={(e) => {
                const deg = parseInt(e.target.value);
                const rad = (deg * Math.PI) / 180;
                onChange({ ...config, startAngle: rad });
              }}
              className={sliderClasses}
            />
            <input
              type="number"
              min="0"
              max="360"
              value={startDeg}
              onChange={(e) => {
                const deg = Math.max(0, Math.min(360, parseInt(e.target.value) || 0));
                const rad = (deg * Math.PI) / 180;
                onChange({ ...config, startAngle: rad });
              }}
              className={numberInputClasses}
            />
            <span className={`${typography.size.sm} ${colors.text.secondary}`}>°</span>
          </div>
        </div>

        {/* End Angle */}
        <div className="space-y-2">
          <label className={`${typography.size.sm} ${colors.text.secondary} ${typography.weight.medium}`}>
            End Angle
          </label>
          <div className="flex items-center gap-3">
            <input
              type="range"
              min="0"
              max="360"
              value={endDeg}
              onChange={(e) => {
                const deg = parseInt(e.target.value);
                const rad = (deg * Math.PI) / 180;
                onChange({ ...config, endAngle: rad });
              }}
              className={sliderClasses}
            />
            <input
              type="number"
              min="0"
              max="360"
              value={endDeg}
              onChange={(e) => {
                const deg = Math.max(0, Math.min(360, parseInt(e.target.value) || 0));
                const rad = (deg * Math.PI) / 180;
                onChange({ ...config, endAngle: rad });
              }}
              className={numberInputClasses}
            />
            <span className={`${typography.size.sm} ${colors.text.secondary}`}>°</span>
          </div>
        </div>
      </div>
    );
  }

  return null;
};
